/**
 * App service worker registration + update toast (K lifecycle).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

const SW_URL = '/sw.js'

async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  try {
    return await navigator.serviceWorker.register(SW_URL, { scope: '/' })
  } catch (err) {
    console.warn('[SW] registration failed:', err)
    return null
  }
}

function watchRegistration(reg: ServiceWorkerRegistration, onUpdate: () => void) {
  if (reg.waiting && navigator.serviceWorker.controller) onUpdate()

  reg.addEventListener('updatefound', () => {
    const worker = reg.installing
    if (!worker) return
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed' && navigator.serviceWorker.controller) onUpdate()
    })
  })
}

export function ServiceWorkerUpdateToast() {
  const [visible, setVisible] = useState(false)
  const regRef = useRef<ServiceWorkerRegistration | null>(null)
  const location = useLocation()

  const showUpdate = useCallback(() => setVisible(true), [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const reg = await getRegistration()
      if (cancelled || !reg) return
      regRef.current = reg
      watchRegistration(reg, showUpdate)
    })()

    window.addEventListener('sw-update-available', showUpdate)
    return () => {
      cancelled = true
      window.removeEventListener('sw-update-available', showUpdate)
    }
  }, [showUpdate])

  useEffect(() => {
    const reg = regRef.current
    if (reg) void reg.update().catch(() => {})
  }, [location.pathname])

  const refresh = () => {
    const reg = regRef.current
    const waiting = reg?.waiting
    if (!waiting) {
      window.location.reload()
      return
    }
    navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true })
    waiting.postMessage({ type: 'SKIP_WAITING' })
  }

  if (!visible) return null

  return (
    <div
      data-testid="sw-update-toast"
      role="status"
      style={{
        position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
        zIndex: 9999, display: 'flex', alignItems: 'center', gap: 12,
        padding: '10px 14px', borderRadius: 8, background: 'var(--ink)', color: '#fff',
        boxShadow: '0 4px 16px rgba(0,0,0,.18)', fontSize: 13,
      }}
    >
      <span>A new version is available — Refresh</span>
      <button type="button" onClick={refresh} data-testid="sw-update-refresh"
        style={{ background: '#fff', color: 'var(--ink)', border: 'none', borderRadius: 6,
          padding: '6px 10px', fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>
        Refresh
      </button>
    </div>
  )
}
